import CloseIcon from 'components/icons/CloseIcon';
import { Emojis } from 'constants/constants';
import React, { MouseEvent, PropsWithChildren } from 'react';
import { ASComponentSize } from 'types/App.types';
import styles from './IconButton.module.scss';

type ColorTheme = 'WhiteTheme' | 'GreenTheme' | 'BlueTheme';

type IconButtonOwnProps = {
  className?: string;
  ref?: React.RefObject<HTMLButtonElement>;
  size?: ASComponentSize;
  defaultLowOpacity?: boolean;
  colorTheme?: ColorTheme;
  selected?: boolean;
  disabled?: boolean;
  withText?: boolean;
  bgColorToken?: ColorTheme;
} & React.HTMLAttributes<HTMLButtonElement>;

// 🞬 📝

const SizeStyles = {
  [ASComponentSize.Small]: styles.ASSmall,
  [ASComponentSize.Medium]: styles.ASMedium,
  [ASComponentSize.Large]: styles.ASLarge,
};

type IconButtonProps = PropsWithChildren<IconButtonOwnProps>;
const IconButton: React.FC<IconButtonProps> = ({
  className = '',
  children,
  size = ASComponentSize.Small,
  defaultLowOpacity = false,
  selected = false,
  withText = false,
  colorTheme = 'WhiteTheme',
  disabled,
  bgColorToken = 'WhiteTheme',
  ...props
}) => {
  const baseCls = `${styles.IconButton} ${className} ${colorTheme} ${bgColorToken}`;
  const withTextCls = withText ? styles.WithText : styles.WithoutText;
  const lowOpacityCls = defaultLowOpacity ? styles.LowOpacity : '';
  const selectedCls = selected ? styles.Selected : '';

  return (
    <button
      type="button"
      {...props}
      disabled={disabled}
      className={`${baseCls} ${lowOpacityCls} ${selectedCls} ${SizeStyles[size]} ${withTextCls}`}
    >
      {children}
    </button>
  );
};

// eslint-disable-next-line react/prop-types
const CloseButton: React.FC<IconButtonOwnProps> = ({ className = '', defaultLowOpacity = true, onClick, ...props }) => {
  const onCloseHandler = (e: MouseEvent<HTMLButtonElement>) => {
    if (onClick) onClick(e);
  };

  return (
    <IconButton
      {...props}
      onClick={onCloseHandler}
      size={props.size || ASComponentSize.Small}
      defaultLowOpacity={defaultLowOpacity}
      className={`${className || ''} ${styles.CloseButton} ${styles.CloseButtonPosition}`}
    >
      <CloseIcon size={40} color="#ffffff" circleStrokeColor="#fff8dc65" />
    </IconButton>
  );
};

type FullScreenButtonProps = {
  isPainting?: boolean;
} & IconButtonOwnProps;

const FullScreenButton: React.FC<FullScreenButtonProps> = ({
  className = '',
  defaultLowOpacity = true,
  isPainting = false,
  // eslint-disable-next-line react/prop-types
  onClick,
  ...props
}) => {
  const positionCls = isPainting ? styles.FullScreenButtonPaintingPosition : styles.FullScreenButtonFramePosition;

  const onCloseHandler = (e: MouseEvent<HTMLButtonElement>) => {
    if (onClick) onClick(e);
  };

  return (
    <IconButton
      {...props}
      onClick={onCloseHandler}
      size={props.size || ASComponentSize.Small}
      defaultLowOpacity={defaultLowOpacity}
      className={`${className || ''} ${styles.FullScreenButton} ${positionCls}`}
    >
      {Emojis.fullWindow}
    </IconButton>
  );
};

export { CloseButton, IconButton as default, FullScreenButton };
